import Color from "color";
import { css } from "./styled";

export const colors = {
    black: "#1B1C1D",
    fullBlack: "#000",
    white: "#fff",
    darkWhite: "#f8f8f8",
    red: "#DB2828",
    orange: "#F2711C",
    olive: "#B5CC18",
    green: "#00897B",
    teal: "#00B5AD",
    blue: "#2185D0",
    violet: "#6435C9",
    purple: "#924c9f",
    pink: "#E03997",
    brown: "#A5673F",
    grey: "#767676",
    yellow: "#FFFF00",
};

export function createTransitions() {
    const transitions = {
        easing: {
            easeInOut: "cubic-bezier(0.4, 0, 0.2, 1)",
            easeOut: "cubic-bezier(0.0, 0, 0.2, 1)",
            easeIn: "cubic-bezier(0.4, 0, 1, 1)",
            sharp: "cubic-bezier(0.4, 0, 0.6, 1)",
        },
        durations: {
            shortest: 150,
            shorter: 200,
            short: 250,
            standard: 300,
            complex: 375,
            enteringScreen: 225,
            leavingScreen: 195,
        },
    };
    return {
        ...transitions,
        create: (
            props: string | string[] = ["all"],
            options: { duration?: number; easing?: string; delay?: number } = {},
        ): string => {
            const {
                duration: durationOption = transitions.durations.standard,
                easing: easingOption = transitions.easing.easeInOut,
                delay = 0,
            } = options;
            return (Array.isArray(props) ? props : [props])
                .map(prop => `${prop} ${durationOption}ms ${easingOption} ${delay}ms`)
                .join(",");
        },
    };
}

export const transitions = createTransitions();

export interface BreakpointValues {
    xs: number;
    xsm?: number;
    sm: number;
    tb: number;
    md: number;
    lg: number;
    xl: number;
}

function breakpointUp(key: keyof BreakpointValues | number, media?: false): string;
function breakpointUp(
    key: keyof BreakpointValues | number,
    media: string | any[] | ReturnType<typeof css>,
): ReturnType<typeof css>;
function breakpointUp(
    key: keyof BreakpointValues | number,
    media?: false | string | any[] | ReturnType<typeof css>,
): string | ReturnType<typeof css> {
    let val = 0;
    if (key !== "xs") {
        val = breakpoints.values[key] || key;
    }
    const str = `(min-width: ${val}px)`;
    if (!media) {
        return str;
    }
    return css`
        @media ${str} {
            ${media};
        }
    `;
}

function breakpointDown(key: keyof BreakpointValues | number, media?: false): string;
function breakpointDown(
    key: keyof BreakpointValues | number,
    media: string | any[] | ReturnType<typeof css>,
): ReturnType<typeof css>;
function breakpointDown(
    key: keyof BreakpointValues | number,
    media?: false | string | any[] | ReturnType<typeof css>,
): string | ReturnType<typeof css> {
    const val: number = breakpoints.values[key] || key;
    const str = `(max-width: ${val - 0.01}px)`;
    if (!media) {
        return str;
    }
    return css`
        @media ${str} {
            ${media};
        }
    `;
}

function breakpointBetween(start: keyof BreakpointValues | number, end: keyof BreakpointValues | number, media?: false): string;
function breakpointBetween(
    start: keyof BreakpointValues | number,
    end: keyof BreakpointValues | number,
    media: string | any[] | ReturnType<typeof css>,
): ReturnType<typeof css>;
function breakpointBetween(
    start: keyof BreakpointValues | number,
    end: keyof BreakpointValues | number,
    media?: false | string | any[] | ReturnType<typeof css>,
): string | ReturnType<typeof css> {
    const startVal: number = breakpoints.values[start] || start;
    const endVal: number = breakpoints.values[end] || end;
    const str = `(min-width: ${startVal}px) and (max-width: ${endVal - 0.01}px)`;

    if (!media) {
        return str;
    }
    return css`
        @media ${str} {
            ${media};
        }
    `;
}

export function createBreakpoints() {
    const breakpoints = {
        values: {
            xs: 360,
            xsm: 450,
            sm: 600,
            tb: 768,
            md: 960,
            lg: 1280,
            xl: 1920,
        },
        up: breakpointUp,
        down: breakpointDown,
        between: breakpointBetween,
    };
    return breakpoints;
}

export const breakpoints = createBreakpoints();

export interface UIThemeInterface {
    colors: {
        primary: string;
        secondary: string;
        danger: string;
        primaryText: string;
        secondaryText: string;
        divider: string;
        darkText: string;
        whiteText: string;
        background: string;
        invertedBackground: string;
    };
    zIndex: {
        dimmer: number;
        defaultPortal: number;
        pageDimmer: number;
        portalModal: number;
        modal: number;
        popover: number;
    };
    dimmer: {
        black: string;
        white: string;
        blurAmount: string;
        grayAmount: number;
    };
    lightenOrDarken(c: string, ratio: number, darkRatio?: number): string;
    getColor(color: keyof this["colors"]): string;
    getColor(color: string): string;
    getColor(color?: string): string | undefined;
    getContrastTextColor(c?: keyof this["colors"] | string): string;
}

export type ColorValue = string | keyof UIThemeInterface["colors"];

export function getBaseTheme(): UIThemeInterface {
    const basicTheme: UIThemeInterface = {
        colors: {
            primary: colors.purple,
            secondary: colors.teal,
            danger: colors.red,
            primaryText: colors.black,
            secondaryText: colors.grey,
            divider: Color(colors.grey).darken(0.7).alpha(0.2).string(),
            darkText: colors.black,
            whiteText: colors.darkWhite,
            background: colors.white,
            invertedBackground: colors.black,
        },
        zIndex: {
            dimmer: 500,
            defaultPortal: 999,
            pageDimmer: 1000,
            portalModal: 1001,
            modal: 1002,
            popover: 2000,
        },
        dimmer: {
            black: Color(colors.fullBlack).alpha(0.85).string(),
            white: Color(colors.white).alpha(0.85).string(),
            blurAmount: "5px",
            grayAmount: 0.7,
        },
        lightenOrDarken(c, ratio, darkRatio = 0.5) {
            const color = Color(c);
            const rgb = color.rgb().array();
            const yiq = (rgb[0] * 299 + rgb[1] * 587 + rgb[2] * 114) / 1000;
            const isDark = yiq < darkRatio * 255;
            return isDark ? color.lighten(ratio).string() : color.darken(ratio).string();
        },
        getColor(color: any) {
            if (!color) {
                return undefined;
            }
            if (typeof color === "function") {
                return color(this.colors);
            }
            if (this.colors[color]) {
                return this.colors[color];
            }
            return color;
        },
        getContrastTextColor(c) {
            if (!c) {
                return this.colors.primaryText;
            }
            try {
                const color = Color(this.getColor(c));
                return color.isDark() ? this.colors.whiteText : this.colors.darkText;
            } catch {
                return this.colors.primaryText;
            }
        },
    };

    return basicTheme;
}

export type PortalUIChatType =
    | "incoming"
    | "active"
    | "answered"
    | "missed"
    | "message"
    | "group";

interface PortalThemeInterface {
    colors: {
        warning: string;
        info: string;
        mainBackground: string;
        contentBackground: string;
        contentBorder: string;
        white: string;
        grayText: string;
        grayRGBA: string;
        gray1: string;
        blue: string;
        blue1: string;
        red: string;
        red1: string;
        divider: string;
        lightGrayText: string;
        blackText: string;
        errorBackground: string;
    };
    elements: {
        headerFullSize: number;
        headerSmallSize: number;
        sidebarSmallSize: number;
        sidebarFullSize: number;
    };
    notifications: {
        infoColor: string;
        warningColor: string;
        errorColor: string;
    };
}

export type ThemeInterface = UIThemeInterface & PortalThemeInterface;

/**
 * Return theme
 */
export function getTheme(): ThemeInterface {
    const uiTheme = getBaseTheme();
    uiTheme.colors.primary = "#3B97B1";
    uiTheme.colors.secondary = "#BBBABB";
    uiTheme.colors.whiteText = colors.white;
    uiTheme.colors.primaryText = colors.black;
    uiTheme.colors.background = colors.white;
    return {
        ...uiTheme,
        colors: {
            ...uiTheme.colors,
            warning: "#fbbd08",
            info: colors.green,
            mainBackground: "#EEEEEE",
            contentBackground: colors.white,
            contentBorder: Color(uiTheme.colors.divider).alpha(0.15).string(),
            white: "#ffffff",
            grayText: "#969696",
            grayRGBA: "rgba(27,28,29,0.87)",
            gray1: "#aaaaaa",
            blue: "#338daa",
            blue1: "#c6c6c6",
            red: "#ff6084",
            red1: "#f3dfe4",
            lightGrayText: "#CCCCCC",
            divider: "#3B97B166",
            blackText: "#000000",
            errorBackground: "rgba(255, 0, 0, 0.4)",
        },
        elements: {
            headerFullSize: 65,
            headerSmallSize: 65,
            sidebarSmallSize: 65,
            sidebarFullSize: 250,
        },
        notifications: {
            infoColor: "rgb(0, 137, 123)",
            warningColor: "rgb(251, 189, 8)",
            errorColor: "rgb(219, 40, 40)",
        },
    };
}

export const theme = getTheme();
